import { ORDER_TYPE } from '@exam-management/common/utils';
import { FilterOperator, useGetDraftQSetsQuery } from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import type { GridCellParams, GridRowSelectionModel } from '@shared/components';

import { BaseQSetList } from '../../components';
import { useQSetListSearchParams } from '../../hooks';
import { useQSetListModal } from '../../providers';

import { columns } from './columns';
import DraftQSetListNoRowsOverlay from './draft-qset-list-no-rows-overlay';

export default function DraftQSetList() {
  const { courseId } = useParams('/exam-courses/:courseId/qsets');
  const navigate = useNavigate();
  const { onSelectQSetIds } = useQSetListModal();
  const [
    { page, pageSize, status, sortOrder, sortBy },
    { changeSorting, changePageSize, changePageNumber },
  ] = useQSetListSearchParams();

  const { data: draftQSetsResult, loading: isSearchQSetsLoading } = useGetDraftQSetsQuery({
    variables: {
      searchQSetsInput: {
        paging: {
          pageSize: pageSize,
          currentPage: page,
        },
        order: {
          type: ORDER_TYPE[sortOrder],
          field: sortBy,
        },
        filter: {
          conditions: [
            {
              value: courseId,
              operator: FilterOperator.Equal,
              field: 'examCourseId',
            },
            {
              value: status,
              operator: FilterOperator.Equal,
              field: 'status',
            },
          ],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const totalQSets = draftQSetsResult?.excSearchQSets.total ?? 0;
  const qSets = (draftQSetsResult?.excSearchQSets.qSets ?? []).map((qSet) => ({
    id: qSet.id,
    code: qSet.code,
    status: qSet.status,
    updatedAt: new Date(qSet.updatedAt),
    qConfig: {
      code: qSet.qConfig.code,
      name: qSet.qConfig.name,
    },
  }));

  const handleCellClick = (cell: GridCellParams) => {
    if (!['moreAction', '__check__'].includes(cell.field)) {
      navigate('/exam-courses/:courseId/qsets/:qSetId', {
        params: {
          courseId,
          qSetId: cell.row.id,
        },
      });
    }
  };

  const handleRowSelectionModelChange = (selection: GridRowSelectionModel) => {
    onSelectQSetIds(selection.map(String));
  };

  return (
    <BaseQSetList
      columns={columns}
      rows={qSets}
      hideFooter={totalQSets === 0}
      loading={isSearchQSetsLoading}
      checkboxSelection
      onCellClick={handleCellClick}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      keepNonExistentRowsSelected
      slots={{
        noRowsOverlay: DraftQSetListNoRowsOverlay,
      }}
      slotProps={{
        footer: {
          total: totalQSets,
          pagination: {
            page: page,
            count: totalQSets,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];

        if (sortModel?.sort && sortModel?.field) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }
        // Reset sorting to default
        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
